import { useCallback, useRef, useState } from 'react';

import type { AbstractError } from '@/domain/errors/abstract-error.js';
import type { JiraEpicOption } from '@/domain/models/development.js';
import type { DevelopmentDetailController } from '@/presentation/controllers/development-detail-controller.js';

type State = {
    open: boolean;
    loading: boolean;
    submitting: boolean;
    suggestion: JiraEpicOption | null;
    epics: JiraEpicOption[];
    occupiedError: AbstractError | null;
    failureMessage: string | null;
    generation: number;
};

const INITIAL: State = {
    open: false,
    loading: false,
    submitting: false,
    suggestion: null,
    epics: [],
    occupiedError: null,
    failureMessage: null,
    generation: 0
};

export function useAssistedJiraLink(controller: DevelopmentDetailController, developmentId: string, onLinked: () => Promise<void>) {
    const [state, setState] = useState<State>(INITIAL);
    const listRequest = useRef(0);

    const close = useCallback(() => {
        if (state.submitting) {
            return;
        }
        listRequest.current += 1;
        setState(INITIAL);
    }, [state.submitting]);

    const openDialog = useCallback(async () => {
        const requestId = listRequest.current + 1;
        listRequest.current = requestId;
        setState((current) => ({ ...INITIAL, open: true, loading: true, generation: current.generation + 1 }));
        const result = await controller.listJiraEpics(developmentId);
        if (listRequest.current !== requestId) {
            return;
        }
        result.fold(
            (error) => {
                setState((current) => ({ ...current, loading: false, failureMessage: error.message }));
            },
            (value) => {
                setState((current) => ({ ...current, loading: false, suggestion: value.suggestion, epics: value.epics }));
            }
        );
    }, [controller, developmentId]);

    const confirm = useCallback(
        async (epicKey: string) => {
            setState((current) => ({ ...current, submitting: true, occupiedError: null, failureMessage: null }));
            const result = await controller.linkJiraEpic(developmentId, epicKey);
            if (result.isLeft()) {
                setState((current) => ({ ...current, submitting: false, occupiedError: result.value }));
                return;
            }
            setState(INITIAL);
            await onLinked();
        },
        [controller, developmentId, onLinked]
    );

    const confirmNoPair = useCallback(async () => {
        setState((current) => ({ ...current, submitting: true, occupiedError: null, failureMessage: null }));
        const result = await controller.confirmNoJiraPair(developmentId);
        if (result.isLeft()) {
            setState((current) => ({ ...current, submitting: false, failureMessage: result.value.message }));
            return;
        }
        setState(INITIAL);
        await onLinked();
    }, [controller, developmentId, onLinked]);

    return { ...state, openDialog, close, confirm, confirmNoPair };
}
